import jsPDF from 'jspdf';
import autoTable, { RowInput } from 'jspdf-autotable';
import orderServices from '../../services/orders/orderservice';
import { POSModel } from '../../models/orders/order-list-model';
import { addPdfPageNumberFooter } from '../../utils/pdf-page-footer';

// Interfaces
interface CategorySummary {
  categoryId: number;
  categoryName: string;
  totalQuantity: number;
  totalGrossAmount: number;
  totalTaxableAmount: number;
  totalTaxAmount: number;
}

interface LineItem {
  challanNo: string;
  categoryId: number;
  categoryName: string;
  productId: number;
  productName: string;
  quantity: number;
  taxableAmount: number;
  taxAmount: number;
  grossAmount: number;
  hsnCode: string;
  createdDate?: string;
  ewayBillNo?: string;
}

interface OutwardChallanPDFProps {
  categorySummaries: CategorySummary[];
  lineItems: LineItem[];
  challanNo?: string;
  fromAddress?: string;
  toAddress?: string;
  generatedBy?: string;
  posData?: POSModel | null;
  drivername?: string;
}

const formatDate = (dateString?: string) => {
  const date = dateString ? new Date(dateString) : new Date();
  if (isNaN(date.getTime())) {
    return new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  }
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const generateOutwardChallanPDF = async ({
  categorySummaries = [],
  lineItems = [],
  challanNo = '',
  fromAddress = 'N/A',
  toAddress = 'N/A',
  generatedBy = 'N/A',
  posData = null,
}: OutwardChallanPDFProps) => {
  // ✅ Filter out negative-value line items and categories
  const validLineItems = lineItems.filter(item => Number(item.grossAmount) >= 0);
  const validCategorySummaries = categorySummaries.filter(cat => Number(cat.totalGrossAmount) >= 0);

  const doc = new jsPDF('p', 'mm', 'a4');
  const leftCol = 14;
  const rightCol = 110;
  const fontFamily = 'helvetica';
  let yPos = 15;
  doc.setFont(fontFamily, 'normal');
  const date = validLineItems[0]?.createdDate ? formatDate(validLineItems[0].createdDate) : formatDate();
  const ewayBillNo = validLineItems[0]?.ewayBillNo || '';

  // Fetch POS data if not provided
  if (!posData) {
    try {
      const response = await orderServices.getPOS();
      posData = response[0] || null;
    } catch (error) {
      console.error('Error fetching POS data:', error);
      posData = null;
    }
  }

  // Header
  doc.setFontSize(16);
  doc.setFont(fontFamily, 'bold');
  doc.text('Outward Delivery Challan', leftCol, yPos);
  yPos += 10;

  // From / To addresses
  doc.setFontSize(11);
  doc.setFont(fontFamily, 'bold');
  doc.text('From:', leftCol, yPos);
  doc.text('Desiri Naturals Private Limited', leftCol, yPos + 6);
  doc.setFont(fontFamily, 'normal');
  doc.text(fromAddress, leftCol, yPos + 12, { maxWidth: 90 });
  doc.text('GST #: 29AAGCD5864J1Z4', leftCol, yPos + 25);

  doc.setFont(fontFamily, 'bold');
  doc.text('To:', rightCol, yPos);
  doc.text('Desiri Naturals Private Limited', rightCol, yPos + 6);
  doc.setFont(fontFamily, 'normal');
  doc.text(toAddress, rightCol, yPos + 12, { maxWidth: 90 });
  doc.text('GST #: 29AAGCD5864J1Z4', rightCol, yPos + 25);
  yPos += 35;

  // ✅ Challan Info + E-Way Bill No (Display in two rows)
doc.setFont(fontFamily, 'bold');

// First row → Challan No + E-Way Bill No
const ewayBillText = ewayBillNo ? `E-Way Bill No: ${ewayBillNo}` : '';
doc.text(`Challan No: ${challanNo}`, leftCol, yPos);
if (ewayBillText) {
  doc.text(ewayBillText, rightCol, yPos);
}
yPos += 7;

// Second row → Generated By + Date
doc.text(`Generated By: ${generatedBy}`, leftCol, yPos);
doc.text(`Date: ${date}`, rightCol, yPos);
yPos += 10;


  doc.setFont(fontFamily, 'normal');

  // Product Details
  doc.setFontSize(14);
  doc.setFont(fontFamily, 'bold');
  doc.text('Product Details', leftCol, yPos);
  doc.setFont(fontFamily, 'normal');
  yPos += 8;

  if (validLineItems.length === 0) {
    doc.text('No products found in the challan.', leftCol, yPos);
    yPos += 10;
  } else {
    // Group line items by category
    const groupedItems: { [key: string]: LineItem[] } = {};
    validLineItems.forEach(item => {
      if (!groupedItems[item.categoryName]) groupedItems[item.categoryName] = [];
      groupedItems[item.categoryName].push(item);
    });

    const body: RowInput[] = [];
    Object.entries(groupedItems).forEach(([categoryName, items]) => {
      body.push([{ content: categoryName, colSpan: 6, styles: { fontStyle: 'bold' } }]);
      items.forEach(item => {
        body.push([
          item.productName || 'N/A',
          item.quantity?.toString() || '0',
          item.hsnCode || 'N/A',
          (item.taxableAmount || 0).toFixed(2),
          (item.taxAmount || 0).toFixed(2),
          (item.grossAmount || 0).toFixed(2),
        ]);
      });

      const categorySummary = validCategorySummaries.find(c => c.categoryName === categoryName);
      body.push([
        {
          content: `${categoryName} Total: ${categorySummary?.totalQuantity || 0} items (Rs.${(categorySummary?.totalGrossAmount || 0).toFixed(2)})`,
          colSpan: 6,
          styles: { fontStyle: 'bold' },
        },
      ]);
    });

    autoTable(doc, {
      head: [['Product', 'Quantity', 'HSN', 'Price Value(Rs)', 'Tax Value(Rs)', 'Total Amount(Rs)']],
      body,
      startY: yPos,
      styles: { fontSize: 10, cellPadding: 3 },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'center' },
        2: { halign: 'center' },
        3: { halign: 'center' },
        4: { halign: 'center' },
        5: { halign: 'center' },
      },
      didDrawPage: data => {
        yPos = data.cursor?.y || yPos + 10;
      },
    });
  }

  // Summary Totals
  yPos += 10;
  doc.setFontSize(14);
  doc.setFont(fontFamily, 'bold');
  doc.text('Summary Totals', leftCol, yPos);
  yPos += 8;

  const priceValueTotal = validCategorySummaries.reduce((sum, c) => sum + (c.totalTaxableAmount || 0), 0);
  const taxValueTotal = validCategorySummaries.reduce((sum, c) => sum + (c.totalTaxAmount || 0), 0);
  const grandTotal = validCategorySummaries.reduce((sum, c) => sum + (c.totalGrossAmount || 0), 0);

  const summaryBody: RowInput[] = validCategorySummaries.map(c => [
    c.categoryName || 'N/A',
    (c.totalTaxableAmount || 0).toFixed(2),
    (c.totalTaxAmount || 0).toFixed(2),
    (c.totalGrossAmount || 0).toFixed(2),
  ]);

  summaryBody.push([
    { content: 'Total', styles: { fontStyle: 'bold', textColor: [0, 0, 255] } },
    { content: priceValueTotal.toFixed(2), styles: { fontStyle: 'bold', textColor: [0, 0, 255] } },
    { content: taxValueTotal.toFixed(2), styles: { fontStyle: 'bold', textColor: [0, 0, 255] } },
    { content: grandTotal.toFixed(2), styles: { fontStyle: 'bold', textColor: [0, 0, 255] } },
  ]);

  autoTable(doc, {
    head: [['Category', 'Total Price Value (Rs)', 'Total Tax Value (Rs)', 'Total Amount (Rs)']],
    body: summaryBody,
    startY: yPos,
    styles: { fontSize: 10, cellPadding: 3 },
    columnStyles: {
      0: { halign: 'left' },
      1: { halign: 'center' },
      2: { halign: 'center' },
      3: { halign: 'center' },
    },
  });

  // Save file
  addPdfPageNumberFooter(doc);
  doc.save(`Outward-Challan-${challanNo || 'Unknown'}.pdf`);
};
